import { Link as LinkIcon } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { fetchUrlMetadata, UrlMetadata } from 'util/fetchUrlMetadata';

interface EntityThumbnailProps {
    url: string;
    urlType: 'img' | 'page';
    isProfilePage: boolean;
    onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    referenceLink?: string;
    onHeightChange?: (height: number) => void;
    maxHeight?: number;
}

const EntityThumbnail: React.FC<EntityThumbnailProps> = ({ 
    url, 
    urlType, 
    onCardClick,
    onHeightChange,
    maxHeight
}) => {
    const [metadata, setMetadata] = useState<UrlMetadata | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        const getMetadata = async () => {
            if (urlType === 'page') {
                const data = await fetchUrlMetadata(url);
                setMetadata(data);
            }
        };

        getMetadata();
    }, [url, urlType]);

    useEffect(() => {
        const image = imageRef.current;
        if (!image) return;

        const handleLoad = () => {
            if (onHeightChange) {
                onHeightChange(image.clientHeight);
            }
        };

        image.addEventListener('load', handleLoad);
        return () => image.removeEventListener('load', handleLoad);
    }, [onHeightChange]);

    const thumbnailClass = `rounded-md bg-transparent max-h-[50vh] h-full object-contain object-center md:object-contain md:object-center md:max-w-[240px] md:max-h-[126px] w-full`;

    if (urlType === 'img') {
        if (!url) return (
            <div className="no-metadata-link flex justify-center items-center w-full md:w-[240px] md:h-[126px] bg-transparent rounded-md" onClick={onCardClick}>
              <LinkIcon className="no-metadata-link size-12 text-eoe-text-2 dark:text-eoe-dark-text-2" />
            </div>
        );

        return (
            <img
                src={url}
                alt="Entity Thumbnail"
                className={thumbnailClass}
                ref={imageRef}
                onClick={onCardClick}
            />
        );
    }

    if (urlType === 'page') {
        if (!metadata?.image) return (
        <div className="no-metadata-link flex justify-center items-center w-full max-h-[50vh] h-full md:w-[240px] md:h-[126px] border-eoe-icon dark:border-eoe-dark-icon border rounded-md" onClick={onCardClick}>
          <LinkIcon className="no-metadata-link size-12 text-eoe-text-2 dark:text-eoe-dark-text-2" />
        </div>
        );

        const imageUrl = metadata.image[0]?.url || metadata.image.url;
        const imageAlt = metadata.title || metadata.image[0]?.alt || 'Link preview';
        
        return (
            <div className="rounded-md flex object-scale-down bg-transparent justify-center items-center">
                <img
                    src={imageUrl}
                    alt={imageAlt}
                    className="rounded-md w-full max-h-[50vh] h-full object-contain object-center md:object-contain md:object-center md:max-w-[240px] md:max-h-[126px]"
                    ref={imageRef}
                    onClick={onCardClick}
                />
            </div>
        );
    }

    return null;
};

export default EntityThumbnail;